import { emit } from '../sim/events.js';
import { stingEntity } from '../sim/damage.js';
import { isSolid, lineHit } from '../terrain/mask.js';
import { solveShot } from '../aim/solver.js';
import type { Bee, GameState, Projectile, WeaponId } from '../types.js';
import { WEAPONS } from './catalog.js';
import { effBeeLifeS, effBeeStingDmg } from './tweaks.js';

/** Rotate a velocity vector by `deg` (world coords, y down; +deg = clockwise). */
export function rotateVel(vx: number, vy: number, deg: number): { x: number; y: number } {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: vx * c - vy * s, y: vx * s + vy * c };
}

export function nextProjectileId(state: GameState): number {
  let id = 1;
  for (const p of state.projectiles) if (p.id >= id) id = p.id + 1;
  return id;
}

export function nextHazardId(state: GameState): number {
  let id = 1;
  for (const h of state.hazards) if (h.id >= id) id = h.id + 1;
  return id;
}

export function nextBeeId(state: GameState): number {
  let id = 1;
  for (const b of state.bees) if (b.id >= id) id = b.id + 1;
  return id;
}

export interface SpawnOpts {
  weaponId: WeaponId;
  ownerId: number;
  teamId: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Child pellets/clusters never re-run their parent's behavior. */
  fromPellet?: boolean;
  splitsRemaining?: number;
  fuseS?: number;
  /** Override the weapon's default behavior (eggs are inert until they land). */
  behavior?: Projectile['behavior'];
  /** Homing mark: which enemy the player tapped before firing. */
  lockTargetId?: number | null;
}

/** Push a projectile into the sim and announce it (event carries a copy). */
export function spawnProjectile(state: GameState, opts: SpawnOpts): Projectile {
  const def = WEAPONS[opts.weaponId];
  const p: Projectile = {
    id: nextProjectileId(state),
    weaponId: opts.weaponId,
    ownerId: opts.ownerId,
    teamId: opts.teamId,
    x: opts.x,
    y: opts.y,
    vx: opts.vx,
    vy: opts.vy,
    ageS: 0,
    bounces: 0,
    fuseS: opts.fuseS ?? def.fuseS,
    armS: def.behavior === 'mine' ? 1.2 : -1,
    stuck: false,
    splitsRemaining:
      opts.splitsRemaining ?? (def.behavior === 'cluster' ? def.count : 0),
    fromPellet: opts.fromPellet ?? false,
    trailAcc: 0,
    behavior: opts.behavior ?? def.behavior,
    timerS: def.stageS,
    lockTargetId: opts.lockTargetId ?? null,
  };
  state.projectiles.push(p);
  emit(state, { type: 'projectile_spawn', projectile: { ...p } });
  return p;
}

/** Remove a projectile and announce it. */
export function removeProjectile(state: GameState, p: Projectile): void {
  const i = state.projectiles.indexOf(p);
  if (i >= 0) state.projectiles.splice(i, 1);
  emit(state, { type: 'projectile_gone', projectileId: p.id });
}

/** Spawn a scatter-pellet fan around a launch direction (carrot's triple). */
export function spawnPelletFan(
  state: GameState,
  weaponId: WeaponId,
  ownerId: number,
  teamId: number,
  x: number,
  y: number,
  vx: number,
  vy: number,
): void {
  const def = WEAPONS[weaponId];
  const n = Math.max(1, def.count);
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1; // -1..1
    const v = rotateVel(vx, vy, t * def.spreadDeg);
    spawnProjectile(state, {
      weaponId,
      ownerId,
      teamId,
      x,
      y,
      vx: v.x,
      vy: v.y,
      fromPellet: true,
      splitsRemaining: 0,
    });
  }
}

/** Mild steering toward the nearest enemy within homing range. */
export function steerHoming(state: GameState, p: Projectile, dt: number): void {
  let bx = 0;
  let by = 0;
  let locked = false;

  // A tapped mark wins over the heuristic — the pigeon was *aimed* at someone.
  if (p.lockTargetId != null) {
    const mark = state.entities.find((e) => e.id === p.lockTargetId);
    if (mark && mark.alive) {
      bx = mark.x;
      by = mark.y - 16;
      locked = true;
    }
  }

  if (!locked) {
    let bd = 520; // homing range
    for (const e of state.entities) {
      if (!e.alive || e.teamId === p.teamId) continue;
      const d = Math.hypot(e.x - p.x, e.y - 16 - p.y);
      if (d < bd) {
        bd = d;
        bx = e.x;
        by = e.y - 16;
        locked = true;
      }
    }
    if (!locked) return; // nobody in range
  }

  const speed = Math.hypot(p.vx, p.vy);
  if (speed < 1) return;
  const cur = Math.atan2(p.vy, p.vx);
  // Flutter: birds don't fly rails — weave the desired heading a little so
  // the approach reads as flapping flight, not a laser.
  const flutter = Math.sin((p.ageS + p.id * 0.7) * 7.5) * 0.3;
  const ideal = Math.atan2(by - p.y, bx - p.x) + flutter;
  const dist = Math.hypot(bx - p.x, by - p.y);

  // Can the bird SEE the mark? If yes, terminal guidance straight in — no
  // dodging at all. (A raw lookahead ray overshoots the target into the
  // ground BEHIND it, which read as "blocked" and made her swerve away or
  // plant beside the very thing she was about to hit.)
  let want = ideal;
  let blocked = false;
  const sightHit = lineHit(state.terrain, p.x, p.y, bx, by);
  if (sightHit !== null) {
    // Terrain between bird and mark: sample candidate headings around the
    // ideal and take the clearest, with a sky bias (over beats digging in).
    blocked = true;
    const reach = Math.min(240, 70 + speed * 0.18, dist + 80);
    const clear = (a: number): boolean => {
      const hit = lineHit(
        state.terrain,
        p.x,
        p.y,
        p.x + Math.cos(a) * reach,
        p.y + Math.sin(a) * reach,
      );
      if (!hit) return true;
      // Ground at/after the mark's distance is not an obstacle — that is
      // where the target STANDS; arriving there is the attack.
      return Math.hypot(hit.x - p.x, hit.y - p.y) >= dist - 8;
    };
    const OFFSETS = [0, -0.3, 0.3, -0.65, 0.65, -1.0, 1.0, -1.4, 1.4, -1.9, 1.9, 2.6, -2.6, Math.PI];
    let best = Infinity;
    for (const off of OFFSETS) {
      const a = ideal + off;
      if (!clear(a)) continue;
      // Score: turn cost from the ideal path, minus a reward for heading up
      // (negative vy = sky). A clear climb beats a clear dive.
      const diff = Math.abs(Math.atan2(Math.sin(a - ideal), Math.cos(a - ideal)));
      const sky = Math.max(0, -Math.sin(a)) * 0.45;
      const score = diff - sky;
      if (score < best) {
        best = score;
        want = a;
      }
    }
    // Every candidate blocked (in a pocket): keep the ideal heading and let
    // the bounce handle it — better than oscillating blind.
  }

  let da = want - cur;
  while (da > Math.PI) da -= Math.PI * 2;
  while (da < -Math.PI) da += Math.PI * 2;
  // Tighter than a "mild" seek: the pigeon is a cruise missile, not a kitten.
  // Dodging gets extra authority so a close wall can still be climbed.
  const maxTurn = (blocked ? 11 : 6.5) * dt;
  const a = cur + Math.max(-maxTurn, Math.min(maxTurn, da));
  p.vx = Math.cos(a) * speed;
  p.vy = Math.sin(a) * speed;
}

/** Armed mine: does any living character stand close enough to trigger it? */
export function mineTriggered(state: GameState, p: Projectile): boolean {
  if (p.armS > 0) return false;
  const def = WEAPONS[p.weaponId];
  // Must trip before the target reaches the blast edge, or the blast damages
  // nobody and the mine silently fizzles.
  const trig = def.radius * 0.78;
  for (const e of state.entities) {
    if (!e.alive) continue;
    // Never trip on its own thrower: a mine landing beside the shooter would
    // otherwise detonate the moment it arms.
    if (e.id === p.ownerId) continue;
    if (Math.hypot(e.x - p.x, e.y - 14 - p.y) < trig) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Hens
// ---------------------------------------------------------------------------

/**
 * Drop one egg below the hen. Eggs are `beans` payloads with no behavior of
 * their own — they just fall and splat, so a hen barrage reads as a line of
 * little impacts walking toward whatever the hen flew over.
 */
export function dropEgg(state: GameState, hen: Projectile): void {
  const def = WEAPONS[hen.weaponId];
  const eggId = def.payload ?? 'beans';
  spawnProjectile(state, {
    weaponId: eggId,
    ownerId: hen.ownerId,
    teamId: hen.teamId,
    x: hen.x,
    y: hen.y + 10,
    vx: hen.vx * 0.55,
    vy: Math.max(40, hen.vy * 0.4),
    behavior: 'none',
    splitsRemaining: 0,
  });
}

// ---------------------------------------------------------------------------
// Beans
// ---------------------------------------------------------------------------

/**
 * One pod of a beans burst. The launcher never leaves the shooter's hands:
 * each pod REPLAYS the launch the burst carries — the lead's own shot, or
 * the preview's launch after a drag re-aim — so the stream flies the exact
 * arc the player aimed, pod for pod. (Solving every pod toward a landing
 * point instead lofted minimum-energy lob the lead never flew and walked
 * short shots past the mark — the burst read as random.) Spawned from the
 * owner's LIVE muzzle (they may have jumped or been shoved since the burst
 * opened); the stored fire-time muzzle is the fallback if they're gone.
 *
 * No launch on record (an `aim` intent arrived carrying only a point): fall
 * back to solving ballistically toward the aim point.
 */
export function spawnVolleyShot(state: GameState): void {
  const v = state.volley;
  if (!v) return;
  const def = WEAPONS[v.weaponId];
  const owner = state.entities.find((e) => e.id === v.ownerId);
  const ox = owner && owner.alive ? owner.x : v.ox;
  const oy = owner && owner.alive ? owner.y - 24 : v.oy;

  if (v.launchVx != null && v.launchVy != null) {
    spawnProjectile(state, {
      weaponId: v.weaponId,
      ownerId: v.ownerId,
      teamId: v.teamId,
      x: ox,
      y: oy,
      vx: v.launchVx,
      vy: v.launchVy,
      behavior: 'none',
      fuseS: -1,
      splitsRemaining: 0,
    });
    return;
  }

  // Keep the target a sane distance from the muzzle: an aim point dropped at
  // the shooter's own feet is a degenerate target the solver can't read, so
  // push it out along the same direction (or downrange of the facing when the
  // direction is nil).
  let tx = v.aimX;
  let ty = v.aimY;
  const dAim = Math.hypot(tx - ox, ty - oy);
  if (dAim < 40) {
    if (dAim > 1) {
      tx = ox + ((tx - ox) / dAim) * 40;
      ty = oy + ((ty - oy) / dAim) * 40;
    } else {
      tx = ox + (owner ? owner.facing : 1) * 60;
      ty = oy - 40;
    }
  }
  const solved = solveShot({ x: ox, y: oy }, { x: tx, y: ty }, state.terrain, state.wind, {
    minSpeed: def.speed * 0.4,
    maxSpeed: def.speed,
    gravityScale: def.gravityScale,
    windScale: def.windScale,
  });
  const d = Math.hypot(tx - ox, ty - oy) || 1;
  const vx = solved ? solved.vx : ((tx - ox) / d) * def.speed * 0.7;
  const vy = solved ? solved.vy : ((ty - oy) / d) * def.speed * 0.7;
  spawnProjectile(state, {
    weaponId: v.weaponId,
    ownerId: v.ownerId,
    teamId: v.teamId,
    x: ox,
    y: oy,
    vx,
    vy,
    behavior: 'none',
    fuseS: -1,
    splitsRemaining: 0,
  });
}

// ---------------------------------------------------------------------------
// Bees
// ---------------------------------------------------------------------------

// Short window + soft sting: the swarm is pressure that expires before the
// next player's aiming phase drags on, not a portable execution squad. The
// menu can retune both (life window + sting power) per match.
const BEE_SPEED = 190;
const BEE_SEEK_RANGE = 420;
/** Stings land on a cooldown so a swarm is a nuisance, not an execution. */
const BEE_STING_CD_S = 0.7;

/** Pick the closest living enemy of `teamId` to (x, y), or null. */
function nearestEnemyTo(state: GameState, teamId: number, x: number, y: number, range: number): number | null {
  let best: number | null = null;
  let bd = range;
  for (const e of state.entities) {
    if (!e.alive || e.teamId === teamId) continue;
    const d = Math.hypot(e.x - x, e.y - 16 - y);
    if (d < bd) {
      bd = d;
      best = e.id;
    }
  }
  return best;
}

/** Burst a hive into a swarm scattered around the impact point. */
export function releaseBees(state: GameState, p: Projectile): void {
  const def = WEAPONS[p.weaponId];
  const n = Math.max(1, def.count);
  for (let i = 0; i < n; i++) {
    // Deterministic spread: evenly fanned directions, no rng draw.
    const a = (i / n) * Math.PI * 2;
    const bee: Bee = {
      id: nextBeeId(state),
      teamId: p.teamId,
      ownerId: p.ownerId,
      x: p.x,
      y: p.y,
      vx: Math.cos(a) * 90,
      vy: Math.sin(a) * 90 - 40,
      targetId: null,
      lifeS: effBeeLifeS(state.config),
      stingCooldownS: 0,
    };
    state.bees.push(bee);
    emit(state, { type: 'bee_spawn', bee: { ...bee } });
  }
}

/**
 * Fly each bee toward its target and sting on contact. Bees path straight
 * (bouncing off terrain) rather than solving a route — they're meant to be a
 * persistent, slightly chaotic pressure, not an aiming system.
 */
export function stepBees(state: GameState, dt: number): void {
  const keep: Bee[] = [];
  for (const b of state.bees) {
    b.lifeS -= dt;
    b.stingCooldownS = Math.max(0, b.stingCooldownS - dt);

    // Re-target when idle, dead, or out of range.
    const cur = b.targetId != null ? state.entities.find((e) => e.id === b.targetId) : null;
    if (!cur || !cur.alive || Math.hypot(cur.x - b.x, cur.y - b.y) > BEE_SEEK_RANGE * 1.5) {
      b.targetId = nearestEnemyTo(state, b.teamId, b.x, b.y, BEE_SEEK_RANGE);
    }
    const target = b.targetId != null ? state.entities.find((e) => e.id === b.targetId) : null;

    if (target && target.alive) {
      const dx = target.x - b.x;
      const dy = target.y - 16 - b.y;
      const d = Math.hypot(dx, dy) || 1;
      // Flutter: a sine wobble perpendicular to the heading, phase from id.
      const wob = Math.sin((b.lifeS + b.id) * 9) * 60;
      const tx = (dx / d) * BEE_SPEED - (dy / d) * wob;
      const ty = (dy / d) * BEE_SPEED + (dx / d) * wob;
      b.vx += (tx - b.vx) * Math.min(1, dt * 5);
      b.vy += (ty - b.vy) * Math.min(1, dt * 5);
    } else {
      b.vy += 60 * dt; // idle drift downward
      b.vx *= 0.98;
    }

    // Terrain: cheap slide — if the next step is inside rock, back off.
    const nx = b.x + b.vx * dt;
    const ny = b.y + b.vy * dt;
    if (isSolid(state.terrain, nx, ny)) {
      if (!isSolid(state.terrain, nx, b.y)) {
        b.y += (b.vy > 0 ? -1 : 1) * 40 * dt;
      } else if (!isSolid(state.terrain, b.x, ny)) {
        b.x += (b.vx > 0 ? -1 : 1) * 40 * dt;
      } else {
        b.vx = -b.vx;
        b.vy = -b.vy;
      }
    } else {
      b.x = nx;
      b.y = ny;
    }

    // Sting.
    if (target && target.alive && b.stingCooldownS <= 0) {
      if (Math.hypot(target.x - b.x, target.y - 16 - b.y) < 18) {
        b.stingCooldownS = BEE_STING_CD_S;
        const dmg = stingEntity(state, target, effBeeStingDmg(state.config), b.teamId, b.x, b.y);
        // Flavor event for the client (buzz VFX); HP itself rides on `damage`.
        if (dmg > 0) {
          emit(state, {
            type: 'bee_sting',
            entityId: target.id,
            amount: dmg,
            hpAfter: target.hp,
            x: b.x,
            y: b.y,
          });
        }
      }
    }

    if (b.lifeS > 0) keep.push(b);
    else emit(state, { type: 'bee_gone', beeId: b.id });
  }
  state.bees = keep;
}

/** Cheap visual check used by the renderer: is (x,y) inside rock? */
export function solidAt(state: GameState, x: number, y: number): boolean {
  return isSolid(state.terrain, x, y);
}

/** Swept test used by AI aim refinement for fast flat weapons. */
export function firstTerrainHit(
  state: GameState,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): { x: number; y: number } | null {
  const hit = lineHit(state.terrain, x0, y0, x1, y1);
  return hit ? { x: hit.x, y: hit.y } : null;
}
